import { createSelector } from 'redux-orm';
import isUndefined from 'lodash/isUndefined';

import orm from '../orm';
import Config from '../constants/Config';

export const selectAccessToken = ({ auth: { accessToken } }) => accessToken;

export const selectIsLogouting = ({ core: { isLogouting } }) => isLogouting;

// FAMILY-EXT: матрица Эйзенхауэра — picker квадранта (null или {cardId, options}).
export const selectPendingQuadrantPicker = ({ core: { pendingQuadrantPicker } }) =>
  pendingQuadrantPicker;

const nextPosition = (items, index, excludedId) => {
  const filteredItems = isUndefined(excludedId)
    ? items
    : items.filter((item) => item.id !== excludedId);

  if (isUndefined(index)) {
    const lastItem = filteredItems[filteredItems.length - 1];

    return (lastItem ? lastItem.position : 0) + Config.POSITION_GAP;
  }

  const prevItem = filteredItems[index - 1];
  const nextItem = filteredItems[index];

  const prevPosition = prevItem ? prevItem.position : 0;

  if (!nextItem) {
    return prevPosition + Config.POSITION_GAP;
  }

  return prevPosition + (nextItem.position - prevPosition) / 2;
};

export const selectNextBoardPosition = createSelector(
  orm,
  (_, projectId) => projectId,
  (_, __, index) => index,
  (_, __, ___, excludedId) => excludedId,
  ({ Project }, projectId, index, excludedId) => {
    const projectModel = Project.withId(projectId);

    if (!projectModel) {
      return projectModel;
    }

    return nextPosition(projectModel.getOrderedBoardsQuerySet().toRefArray(), index, excludedId);
  },
);

export const selectNextLabelPosition = createSelector(
  orm,
  (_, boardId) => boardId,
  (_, __, index) => index,
  (_, __, ___, excludedId) => excludedId,
  ({ Board }, boardId, index, excludedId) => {
    const boardModel = Board.withId(boardId);

    if (!boardModel) {
      return boardModel;
    }

    return nextPosition(boardModel.getOrderedLabelsQuerySet().toRefArray(), index, excludedId);
  },
);

export const selectNextListPosition = createSelector(
  orm,
  (_, boardId) => boardId,
  (_, __, index) => index,
  (_, __, ___, excludedId) => excludedId,
  ({ Board }, boardId, index, excludedId) => {
    const boardModel = Board.withId(boardId);

    if (!boardModel) {
      return boardModel;
    }

    return nextPosition(boardModel.getOrderedListsQuerySet().toRefArray(), index, excludedId);
  },
);

export const selectNextCardPosition = createSelector(
  orm,
  (_, listId) => listId,
  (_, __, index) => index,
  (_, __, ___, excludedId) => excludedId,
  ({ List }, listId, index, excludedId) => {
    const listModel = List.withId(listId);

    if (!listModel) {
      return listModel;
    }

    return nextPosition(listModel.getFilteredOrderedCardsModelArray(), index, excludedId);
  },
);

// FAMILY-EXT: матрица Эйзенхауэра — контекст вставки при дропе в default-
// режиме. Возвращает:
//   { ambiguous: true, options: [qA, qB] } — если сосед-сверху и сосед-
//      снизу принадлежат РАЗНЫМ непустым квадрантам → нужно спросить.
//   { ambiguous: false, quadrant: 'qN'|null } — однозначный кейс,
//      возвращаем какой квадрант адоптировать (или null = не трогать).
export const selectInsertionContext = createSelector(
  orm,
  (_, listId) => listId,
  (_, __, index) => index,
  (_, __, ___, excludedId) => excludedId,
  ({ List }, listId, index, excludedId) => {
    const listModel = List.withId(listId);
    if (!listModel) {
      return { ambiguous: false, quadrant: null };
    }

    const cards = listModel
      .getFilteredOrderedCardsModelArray()
      .filter((c) => c.id !== excludedId);

    const above = cards[index - 1];
    const below = cards[index];
    const aboveQ = above && above.eisenhowerQuadrant;
    const belowQ = below && below.eisenhowerQuadrant;

    if (aboveQ && belowQ && aboveQ !== belowQ) {
      return { ambiguous: true, options: [aboveQ, belowQ] };
    }
    return { ambiguous: false, quadrant: aboveQ || belowQ || null };
  },
);

// FAMILY-EXT: матрица Эйзенхауэра — позиция при дропе внутри конкретного
// квадранта. Учитывает только карточки этого квадранта в списке, чтобы
// reorder внутри квадранта сохранялся (а не "прыгал" после drop).
export const selectNextCardPositionInQuadrant = createSelector(
  orm,
  (_, listId) => listId,
  (_, __, quadrant) => quadrant,
  (_, __, ___, index) => index,
  (_, __, ___, ____, excludedId) => excludedId,
  ({ List }, listId, quadrant, index, excludedId) => {
    const listModel = List.withId(listId);

    if (!listModel) {
      return listModel;
    }

    return nextPosition(
      listModel.getFilteredOrderedCardsModelArrayInQuadrant(quadrant),
      index,
      excludedId,
    );
  },
);

export const selectNextTaskPosition = createSelector(
  orm,
  (_, cardId) => cardId,
  (_, __, index) => index,
  (_, __, ___, excludedId) => excludedId,
  ({ Card }, cardId, index, excludedId) => {
    const cardModel = Card.withId(cardId);

    if (!cardModel) {
      return cardModel;
    }

    return nextPosition(cardModel.getOrderedTasksQuerySet().toRefArray(), index, excludedId);
  },
);

export default {
  selectAccessToken,
  selectIsLogouting,
  selectNextBoardPosition,
  selectNextLabelPosition,
  selectNextListPosition,
  selectNextCardPosition,
  selectNextCardPositionInQuadrant,
  selectInsertionContext,
  selectNextTaskPosition,
  selectPendingQuadrantPicker,
};
